import AddBoxSharpIcon from '@mui/icons-material/AddBoxSharp';
import DisabledByDefaultSharpIcon from '@mui/icons-material/DisabledByDefaultSharp';
import { ButtonBase, Stack, Typography } from '@mui/material';
import { Participant } from 'features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import { Perspectives } from 'features/lead/lead-questionnaire/lead-questionnaire.types';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { BasicActionDialog } from '../../../../ui/base-dialogs';

interface Props {
  open: boolean;
  participants: Participant[];
  participantTrigger: boolean;

  formChangeHandlerGenerator(idx: number, type: string): (e: React.ChangeEvent<HTMLInputElement>) => void;

  formChangeHandleDeleteGenerator(idx: number): () => void;

  addEvaluator(): void;

  handleClose(): void;

  handleProceed(): void;
}

const AddParticipantsDialog: React.FC<Props> = ({
  open,
  participants,
  participantTrigger,
  formChangeHandleDeleteGenerator,
  formChangeHandlerGenerator,
  addEvaluator,
  handleProceed,
  handleClose,
}) => {
  const { classes } = useStyles();

  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const nameString = participants.map((participant) => participant.name.trim());
    const roleString = participants.map((participant) => participant.role.trim());

    if (nameString.includes('') || roleString.includes('')) {
      setIsEmpty(true);
    } else {
      setIsEmpty(false);
    }
  }, [participantTrigger]);

  return (
    <BasicActionDialog
      open={open}
      maxWidth={'sm'}
      dialogTitle={'Feedbackgeber hinzufügen'}
      cancelButtonText={'Abbrechen'}
      proceedButtonText={'Hinzufügen'}
      handleClose={handleClose}
      handleProceed={handleProceed}
      proceedButtonDisabled={isEmpty}
    >
      <Stack gap={2}>
        {participants.map((_, idx) => (
          <Stack key={idx} direction={'row'} alignContent={'center'} gap={2}>
            <div style={{ flexBasis: '100%' }}>
              <PaiTextField
                value={participants[idx].name}
                label={'Name'}
                placeholder={'Name des Fremdbewerters...'}
                onChange={formChangeHandlerGenerator(idx, 'name')}
              />
            </div>
            <div style={{ flexBasis: '100%' }}>
              <PaiSelect value={participants[idx].role} label={'Rolle des Feedbackgebers'} onChange={formChangeHandlerGenerator(idx, 'role')}>
                <PaiMenuItem value={Perspectives.BOSS}>Führungskraft</PaiMenuItem>
                <PaiMenuItem value={Perspectives.COLLEAGUE}>Kolleg*in</PaiMenuItem>
                <PaiMenuItem value={Perspectives.EMPLOYEE}>Mitarbeiter*in</PaiMenuItem>
                <PaiMenuItem value={Perspectives.OTHER}>Andere</PaiMenuItem>
              </PaiSelect>
            </div>
            {participants.length > 1 ? (
              <ButtonBase className={classes.deleteButton} onClick={formChangeHandleDeleteGenerator(idx)}>
                <DisabledByDefaultSharpIcon className={classes.icon} />
              </ButtonBase>
            ) : null}
          </Stack>
        ))}
        <ButtonBase className={classes.addButton} onClick={addEvaluator}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
            <AddBoxSharpIcon className={classes.icon} />
            <Typography className={classes.text}>weitere Feedbackgeber</Typography>
          </Stack>
        </ButtonBase>
      </Stack>
    </BasicActionDialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  deleteButton: {
    marginTop: 'calc(19.5px + 8px)',
    color: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  addButton: {
    marginRight: 'auto',
    marginTop: '4px',
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
  },
}));

export default AddParticipantsDialog;
